package com.iptvplayer.shared.presentation.state

import com.iptvplayer.shared.domain.model.SourceFailure
import com.iptvplayer.shared.domain.model.SyncReport

/**
 * Why Home/Browse may be missing content. Every client renders this with the
 * same wording via [title] / [message], so the user always learns whether the
 * problem is their account (no sources), their provider (down / bad
 * credentials), their connection, or our backend.
 */
sealed interface ContentIssue {
    /** The account has no IPTV source configured at all. */
    data object NoSources : ContentIssue

    /** Every configured source failed to load — nothing fresh to show. */
    data class AllSourcesFailed(val failures: List<SourceFailure>) : ContentIssue

    /** Some sources loaded, others failed — content may be incomplete. */
    data class SomeSourcesFailed(val failures: List<SourceFailure>) : ContentIssue

    /** Couldn't fetch the source list from the app's own backend. */
    data class SyncFailed(val detail: String? = null) : ContentIssue
}

/** Map a finished sync to the issue the user should see (null = all good). */
fun SyncReport.toContentIssue(): ContentIssue? = when {
    sourceCount == 0 -> ContentIssue.NoSources
    allFailed -> ContentIssue.AllSourcesFailed(failures)
    failures.isNotEmpty() -> ContentIssue.SomeSourcesFailed(failures)
    else -> null
}

/** True when the fix is adding a source (vs. retrying). */
val ContentIssue.isNoSources: Boolean get() = this is ContentIssue.NoSources

val ContentIssue.title: String
    get() = when (this) {
        is ContentIssue.NoSources -> "No IPTV source yet"
        is ContentIssue.AllSourcesFailed ->
            if (failures.size == 1) "Your IPTV provider isn't responding"
            else "Your IPTV providers aren't responding"
        is ContentIssue.SomeSourcesFailed -> "Some sources failed to load"
        is ContentIssue.SyncFailed -> "Couldn't refresh your sources"
    }

val ContentIssue.message: String
    get() = when (this) {
        is ContentIssue.NoSources ->
            "Your account has no IPTV source configured. Add one to load live channels, movies and series."
        is ContentIssue.AllSourcesFailed -> buildString {
            append(failures.joinToString("\n") { it.describe() })
            append("\n\nThis is a problem with the source, not the app. ")
            append(
                if (failures.all { it.kind == SourceFailure.Kind.NETWORK })
                    "Check your internet connection and try again."
                else
                    "You can retry, or try again later."
            )
        }
        is ContentIssue.SomeSourcesFailed ->
            failures.joinToString("\n") { it.describe() } +
                "\n\nContent from the remaining sources is shown, so lists may be incomplete."
        is ContentIssue.SyncFailed ->
            "The app couldn't reach its server to load your source list." +
                (detail?.let { "\n($it)" } ?: "") +
                "\nCheck your connection and retry."
    }

private fun SourceFailure.describe(): String = "“$sourceName” — " + when (kind) {
    SourceFailure.Kind.PROVIDER_DOWN ->
        "the provider's server is down or returning errors" +
            (detail?.let { " ($it)" } ?: "") + "."
    SourceFailure.Kind.AUTH_FAILED ->
        "the provider rejected this source's username/password."
    SourceFailure.Kind.NETWORK ->
        "the provider can't be reached (network problem or the server is offline)."
    SourceFailure.Kind.UNKNOWN ->
        "it failed with an unexpected error" + (detail?.let { " ($it)" } ?: "") + "."
}
